// ============================================================
// AIRTABLE VIDEO  -  Batch-load ALL fresh MP4 URLs at startup
// ============================================================
// Airtable attachment URLs expire after ~2h.
// This module loads ALL video URLs via the backend proxy,
// then provides instant lookup by airtable_id OR lien (URL).
// ============================================================

import { API_URL, sb, SUPABASE_URL } from './config'

/**
 * Get the Supabase Storage thumbnail URL for a video shortcode.
 * Pattern : {SUPABASE_URL}/storage/v1/object/public/beat-frames/{shortcode}_beat1.jpg
 * URL stable et publique (jamais d'expiration), source de vérité.
 * Si le fichier n'existe pas (analyse pas encore faite côté pipeline),
 * l'img onError pourra fallback sur Airtable thumb ou placeholder.
 */
export function getSupabaseThumb(video) {
  if (!video || !SUPABASE_URL) return null
  const sc = extractShortcode(video.lien)
  if (!sc) return null
  return `${SUPABASE_URL}/storage/v1/object/public/beat-frames/${sc}_beat1.jpg`
}

// Field IDs (no accented names = no encoding issues)
const FIELD_MP4 = 'fldz6V5sZc8hBG06X'   // "Video MP4"  -  attachment
const FIELD_LIEN = 'fldjwDqezR1bFhPa8'   // "Lien"  -  original URL
const FIELD_THUMB = 'fldAOan2RmlnoktXk'  // "Thumbnail"  -  attachment (fallback poster)

// Base ID must match the Supabase environment:
// - dev Supabase (soivikmwtzwohsjkfroq) → Airtable dev (app5aL8L5gO2v9Iyy)
// - prod Supabase (utslaredneufzgkpggmm) → Airtable prod (apprPaS1dtn6vIfMn)
// If not set in env, falls back to dev (safe default for local work).
const AIRTABLE_BASE_ID = import.meta.env.VITE_AIRTABLE_BASE_ID || 'app5aL8L5gO2v9Iyy'
const AIRTABLE_VIDEOS_TABLE = 'tblGMoXq68n83xOaC'
console.log('[airtableVideo] Base ID:', AIRTABLE_BASE_ID)

// All Airtable calls go through the backend proxy
const AIRTABLE_BASE_URL = `${API_URL}/api/airtable`

// Auth header helper (JWT for proxy endpoints) — waits for session if needed
async function getAuthHeaders(retries = 3) {
  for (let i = 0; i < retries; i++) {
    const { data } = await sb.auth.getSession()
    const token = data?.session?.access_token
    if (token) return { 'Authorization': `Bearer ${token}` }
    if (i < retries - 1) await new Promise(r => setTimeout(r, 1000))
  }
  console.warn('[airtableVideo] No auth session available after retries')
  return {}
}

// Cache: maps for instant lookup
let mp4ByRecordId = {}    // recXXX -> mp4 url (Airtable record ID)
let mp4ByLien = {}        // instagram.com/reel/xxx -> mp4 url (URL normalisée)
let mp4ByShortcode = {}   // DXns2qwKSLg -> mp4 url (★ identité Instagram, source de vérité)
let thumbByRecordId = {}  // recXXX -> thumbnail url (fallback poster)
let thumbByLien = {}
let thumbByShortcode = {}
let lastFetchTime = 0
let _loadingPromise = null  // prevent concurrent calls
const CACHE_TTL = 90 * 60 * 1000  // 90 min (Airtable/Instagram URLs expire ~2h)

// Soft threshold: above this age, consumers should trigger a background refresh
// to avoid serving expired URLs
export const STALE_AFTER = 60 * 60 * 1000  // 60 min
export function isCacheStale() { return lastFetchTime > 0 && (Date.now() - lastFetchTime) >= STALE_AFTER }
export function getCacheAgeMs() { return lastFetchTime > 0 ? Date.now() - lastFetchTime : Infinity }

// Force a refresh regardless of current TTL (use after a video 410/expired error, or on visibility return)
export async function refreshAllVideoUrls() {
  lastFetchTime = 0
  _loadingPromise = null
  return loadAllVideoUrls()
}

/**
 * Load ALL video MP4 URLs from Airtable in batch.
 * Routes through backend proxy for auth.
 * Paginates automatically (100 per page).
 */
export async function loadAllVideoUrls() {
  console.log(`[airtableVideo] loadAllVideoUrls called — cache=${!!lastFetchTime}, loading=${!!_loadingPromise}, cacheAge=${lastFetchTime ? Math.round((Date.now()-lastFetchTime)/1000)+'s' : 'none'}`)

  // Return cache if fresh
  if (lastFetchTime && (Date.now() - lastFetchTime) < CACHE_TTL) {
    console.log(`[airtableVideo] ✅ Using cache (${Object.keys(mp4ByRecordId).length} URLs, age: ${Math.round((Date.now() - lastFetchTime)/60000)}min)`)
    return { byId: mp4ByRecordId, byLien: mp4ByLien }
  }

  // Prevent concurrent fetches — if already loading, return the same promise
  if (_loadingPromise) {
    console.log('[airtableVideo] ⏳ Already loading, waiting for existing request...')
    return _loadingPromise
  }

  console.log('[airtableVideo] 🚀 Starting fresh load from Airtable...')

  _loadingPromise = (async () => {
    const newById = {}
    const newByLien = {}
    const newByShortcode = {}
    const newThumbById = {}
    const newThumbByLien = {}
    const newThumbByShortcode = {}
    let offset = null
    let page = 0

    try {
      const authHeaders = await getAuthHeaders()
      console.log('[airtableVideo] Auth headers:', Object.keys(authHeaders).length ? 'JWT present' : '❌ NO AUTH')

      do {
        page++
        let url = `${AIRTABLE_BASE_URL}/${AIRTABLE_BASE_ID}/${AIRTABLE_VIDEOS_TABLE}?returnFieldsByFieldId=true&fields%5B%5D=${FIELD_MP4}&fields%5B%5D=${FIELD_LIEN}&fields%5B%5D=${FIELD_THUMB}&pageSize=100`
        if (offset) url += `&offset=${encodeURIComponent(offset)}`

        console.log(`[airtableVideo] Page ${page}...`)
        const res = await fetch(url, { headers: authHeaders })

        if (!res.ok) {
          const errText = await res.text().catch(() => '')
          console.error(`[airtableVideo] HTTP ${res.status}:`, errText.substring(0, 300))
          break
        }

        const data = await res.json()
        const records = data.records || []

        // Debug: log first record structure on page 1
        if (page === 1 && records.length > 0) {
          const sample = records[0]
          console.log('[airtableVideo] 🔍 Sample record keys:', Object.keys(sample))
          console.log('[airtableVideo] 🔍 Sample record.fields keys:', sample.fields ? Object.keys(sample.fields) : 'NO .fields')
          console.log('[airtableVideo] 🔍 Sample record.cellValuesByFieldId:', sample.cellValuesByFieldId ? Object.keys(sample.cellValuesByFieldId) : 'NO .cellValuesByFieldId')
          console.log('[airtableVideo] 🔍 Looking for FIELD_MP4:', FIELD_MP4, 'FIELD_LIEN:', FIELD_LIEN)
          console.log('[airtableVideo] 🔍 Full first record:', JSON.stringify(sample).substring(0, 500))
        }

        records.forEach(rec => {
          const fields = rec.cellValuesByFieldId || rec.fields || {}
          const attachments = fields[FIELD_MP4]
          const lien = fields[FIELD_LIEN]
          const thumbAttachments = fields[FIELD_THUMB]
          const normalized = lien ? normalizeLien(lien) : null
          const sc = lien ? extractShortcode(lien) : null

          if (attachments && attachments.length > 0) {
            const mp4Url = attachments[0].url
            newById[rec.id] = mp4Url
            if (normalized) newByLien[normalized] = mp4Url
            if (sc) newByShortcode[sc] = mp4Url
          }

          if (thumbAttachments && thumbAttachments.length > 0) {
            const thumbUrl = thumbAttachments[0].url
            newThumbById[rec.id] = thumbUrl
            if (normalized) newThumbByLien[normalized] = thumbUrl
            if (sc) newThumbByShortcode[sc] = thumbUrl
          }
        })

        offset = data.offset || null
        console.log(`[airtableVideo] Page ${page}: ${records.length} records, ${Object.keys(newById).length} with MP4`)
      } while (offset)

      mp4ByRecordId = newById
      mp4ByLien = newByLien
      mp4ByShortcode = newByShortcode
      thumbByRecordId = newThumbById
      thumbByLien = newThumbByLien
      thumbByShortcode = newThumbByShortcode
      lastFetchTime = Date.now()

      console.log(`[airtableVideo] Loaded ${Object.keys(mp4ByRecordId).length} MP4 URLs, ${Object.keys(thumbByShortcode).length} thumbnails`)

      return { byId: mp4ByRecordId, byLien: mp4ByLien, byShortcode: mp4ByShortcode }
    } catch (e) {
      console.error('[airtableVideo] Batch load failed:', e.message || e)
      return { byId: mp4ByRecordId, byLien: mp4ByLien }
    } finally {
      _loadingPromise = null
    }
  })()

  return _loadingPromise
}

/**
 * Get a fresh MP4 URL for a video.
 * Préfère shortcode (identité Instagram, le plus fiable) puis airtable_id puis lien.
 *
 * BUG HISTORIQUE : on tombait sur des cas où le lookup par airtable_id renvoyait
 * le MP4 d'une AUTRE vidéo (ex. duplicate Airtable record, mismatch sync). Le
 * player jouait video B alors que le script était basé sur video A. Le shortcode
 * (ex. "DXns2qwKSLg" extrait de instagram.com/reel/DXns2qwKSLg/) est l'IDENTITÉ
 * Instagram et ne change jamais — donc fiable comme clé primaire.
 *
 * On garde airtable_id + lien comme fallbacks pour les rares cas où le shortcode
 * n'est pas extractible (vidéos hors Instagram, URL malformée).
 */
export function getVideoMp4(video) {
  if (!video) return null

  // Try by shortcode (le plus fiable — identité Instagram)
  const sc = extractShortcode(video.lien)
  if (sc && mp4ByShortcode[sc]) {
    return mp4ByShortcode[sc]
  }

  // Fallback : airtable_id (peut désynchroniser si l'Airtable record a été recréé)
  if (video.airtable_id && mp4ByRecordId[video.airtable_id]) {
    const mp4 = mp4ByRecordId[video.airtable_id]
    // Sanity check : si on a une URL pour cet Airtable record mais qu'il y avait
    // un shortcode dans le lien qu'on n'a pas trouvé dans mp4ByShortcode, le
    // mapping airtable_id→mp4 est suspect (probablement record réutilisé pour
    // une autre vidéo). On préfère null pour forcer le fallback "Voir sur Instagram"
    // (qui utilise sVideo.lien et est toujours correct).
    if (sc && !mp4ByShortcode[sc]) {
      console.warn(`[airtableVideo] ⚠️ airtable_id match but no shortcode match (sc=${sc}, airtable_id=${video.airtable_id}) → suspect mapping, returning null`)
      return null
    }
    return mp4
  }

  // Fallback : lien normalisé
  if (video.lien) {
    const normalized = normalizeLien(video.lien)
    if (mp4ByLien[normalized]) return mp4ByLien[normalized]
    console.warn(`[airtableVideo] ❌ No match for lien="${video.lien}" → normalized="${normalized}", airtable_id="${video.airtable_id}", sc=${sc}, cache size=${Object.keys(mp4ByLien).length}`)
  } else {
    console.warn(`[airtableVideo] ❌ Video has no lien and no airtable_id:`, video.titre || video.id)
  }

  return null
}

/**
 * Get a fresh Thumbnail URL for a video (fallback poster quand pas de MP4).
 * Même résolution que getVideoMp4 : shortcode > airtable_id > lien.
 */
export function getVideoThumb(video) {
  if (!video) return null
  const sc = extractShortcode(video.lien)
  if (sc && thumbByShortcode[sc]) return thumbByShortcode[sc]
  if (video.airtable_id && thumbByRecordId[video.airtable_id]) return thumbByRecordId[video.airtable_id]
  if (video.lien) {
    const normalized = normalizeLien(video.lien)
    if (thumbByLien[normalized]) return thumbByLien[normalized]
  }
  return null
}

/**
 * Extrait le shortcode Instagram d'une URL.
 * Ex: https://www.instagram.com/reel/DXns2qwKSLg/?igsh=... → "DXns2qwKSLg"
 *     https://instagram.com/p/ABC123/ → "ABC123"
 *     URL non-Instagram → null
 */
function extractShortcode(url) {
  if (!url) return null
  const m = String(url).match(/instagram\.com\/(?:reel|p|tv|reels)\/([A-Za-z0-9_-]+)/i)
  return m ? m[1] : null
}

/**
 * Normalize a URL for fuzzy matching.
 * Strips protocol, www, trailing slashes, query params.
 */
function normalizeLien(url) {
  if (!url) return ''
  try {
    const u = new URL(url)
    return (u.hostname + u.pathname).replace(/^www\./, '').replace(/\/+$/, '').toLowerCase()
  } catch {
    return url.toLowerCase().replace(/\/+$/, '')
  }
}

// Legacy exports for backward compatibility
export async function getFreshVideoUrl(airtableId) {
  if (!airtableId) return null
  if (!lastFetchTime) await loadAllVideoUrls()
  return mp4ByRecordId[airtableId] || null
}

export async function prefetchVideoUrls(videos) {
  if (!lastFetchTime) await loadAllVideoUrls()
  return mp4ByRecordId
}
